/**
 * Desktop keyboard/ARIA support for the main nav dropdowns (.no-mega-menu).
 * Isolated from halo.js on purpose — only runs at desktop widths (>=1200px)
 * and never touches halo.js's mobile click-toggle system or its classes
 * beyond reading/writing aria-expanded and a new .js-open class.
 *
 * IMPORTANT: halo.js periodically replaces the entire .site-nav element
 * via jQuery .replaceWith() (AJAX mega-menu / cart refresh). A MutationObserver
 * watches for this and re-binds automatically so keyboard support survives it.
 */
(function () {
    "use strict";

    var DESKTOP_MIN = 1200;
    var boundItems = new WeakSet();

    function isDesktop() {
        return window.innerWidth >= DESKTOP_MIN;
    }

    function getDropdownItems() {
        return document.querySelectorAll(".site-nav .no-mega-menu");
    }

    function getTrigger(item) {
        return item.querySelector(":scope > .menu__moblie");
    }

    function getSubmenu(item) {
        return item.querySelector(":scope > .sub-menu-mobile");
    }

    function getSubmenuLinks(item) {
        var submenu = getSubmenu(item);
        if (!submenu) return [];
        // Exclude the "All X" link (.hide_pc) — it's mobile-only per the
        // theme's own markup and isn't visible/reachable on desktop.
        return Array.prototype.slice.call(
            submenu.querySelectorAll(".site-nav-dropdown li:not(.hide_pc) a")
        );
    }

    // The submenu items render as a multi-column CSS grid on desktop
    // (e.g. CANDY MIX / ASSORTED | CHAMOY... | CHILI... | CHOCOLATE... all
    // in one visual row). DOM order is row-by-row, so plain "next in DOM
    // order" for Down/Up feels like it moves sideways. Detect the real
    // column count from each link's actual on-screen position so Down/Up
    // move down/up a column and Left/Right move across a row, matching
    // what the user sees.
    function getColumnCount(links) {
        if (links.length < 2) return 1;
        var firstTop = links[0].getBoundingClientRect().top;
        var count = 1;
        for (var i = 1; i < links.length; i++) {
            if (Math.abs(links[i].getBoundingClientRect().top - firstTop) < 2) {
                count++;
            } else {
                break;
            }
        }
        return count;
    }

    function openItem(item) {
        if (!isDesktop()) return;
        closeAllExcept(item);
        item.classList.add("js-open");
        var trigger = getTrigger(item);
        if (trigger) trigger.setAttribute("aria-expanded", "true");
    }

    function closeItem(item) {
        item.classList.remove("js-open");
        var trigger = getTrigger(item);
        if (trigger) trigger.setAttribute("aria-expanded", "false");
    }

    function closeAllExcept(exceptItem) {
        getDropdownItems().forEach(function (item) {
            if (item !== exceptItem) closeItem(item);
        });
    }

    function closeAll() {
        getDropdownItems().forEach(closeItem);
    }

    function focusFirstSubmenuLink(item) {
        var links = getSubmenuLinks(item);
        if (!links.length) return;
        // Wait a frame so the browser has applied the .js-open visibility
        // styles before we try to focus — focusing an element that is
        // still visibility:hidden / opacity:0 at call time is silently
        // ignored by the browser, which is why "open + auto-focus" can
        // appear to do nothing without this.
        window.requestAnimationFrame(function () {
            links[0].focus();
        });
    }

    function handleTriggerKeydown(item, trigger) {
        return function (e) {
            if (!isDesktop()) return;

            switch (e.key) {
                case "Enter":
                case " ":
                case "Spacebar": // older browsers
                    e.preventDefault();
                    e.stopPropagation();
                    if (item.classList.contains("js-open")) {
                        closeItem(item);
                    } else {
                        openItem(item);
                        focusFirstSubmenuLink(item);
                    }
                    break;

                case "Escape":
                case "Esc":
                    if (item.classList.contains("js-open")) {
                        closeItem(item);
                        trigger.focus();
                    }
                    break;

                case "ArrowDown":
                    e.preventDefault();
                    openItem(item);
                    focusFirstSubmenuLink(item);
                    break;

                default:
                    break;
            }
        };
    }

    function handleSubmenuKeydown(item) {
        return function (e) {
            if (!isDesktop()) return;

            var links = getSubmenuLinks(item);
            if (!links.length) return;

            var currentIndex = links.indexOf(document.activeElement);
            var columns = getColumnCount(links);

            function moveTo(index) {
                if (index >= 0 && index < links.length) {
                    links[index].focus();
                }
            }

            // Work in row/column terms so movement always lands in a real
            // grid cell — never an out-of-bounds flat-index guess.
            function rowOf(index) {
                return Math.floor(index / columns);
            }
            function colOf(index) {
                return index % columns;
            }
            function indexAt(row, col) {
                var idx = row * columns + col;
                return idx < links.length ? idx : -1;
            }
            function columnLength(col) {
                // how many real items exist in this column (last row may
                // be short if the count doesn't divide evenly)
                var count = 0;
                for (var i = col; i < links.length; i += columns) count++;
                return count;
            }

            switch (e.key) {
                case "ArrowDown":
                    e.preventDefault();
                    if (currentIndex === -1) {
                        moveTo(0);
                    } else {
                        var col = colOf(currentIndex);
                        var row = rowOf(currentIndex);
                        var lastRowInCol = columnLength(col) - 1;
                        if (row < lastRowInCol) {
                            // move down within the same column
                            moveTo(indexAt(row + 1, col));
                        } else {
                            // bottom of this column reached — move to the
                            // TOP of the next column, not the last item
                            var nextCol = col + 1;
                            if (nextCol < columns) {
                                var target = indexAt(0, nextCol);
                                moveTo(target !== -1 ? target : links.length - 1);
                            }
                            // if this was already the last column, do nothing
                            // (stay put) — no more columns to move into
                        }
                    }
                    break;

                case "ArrowUp":
                    e.preventDefault();
                    if (currentIndex === -1) {
                        moveTo(links.length - 1);
                    } else {
                        var upCol = colOf(currentIndex);
                        var upRow = rowOf(currentIndex);
                        if (upRow > 0) {
                            // move up within the same column
                            moveTo(indexAt(upRow - 1, upCol));
                        } else {
                            // top of this column reached — move to the
                            // BOTTOM of the previous column
                            var prevCol = upCol - 1;
                            if (prevCol >= 0) {
                                var prevColLastRow = columnLength(prevCol) - 1;
                                moveTo(indexAt(prevColLastRow, prevCol));
                            }
                        }
                    }
                    break;

                case "ArrowRight":
                    e.preventDefault();
                    if (currentIndex === -1) {
                        moveTo(0);
                    } else if ((currentIndex + 1) % columns !== 0) {
                        moveTo(currentIndex + 1);
                    }
                    break;

                case "ArrowLeft":
                    e.preventDefault();
                    if (currentIndex === -1) {
                        moveTo(0);
                    } else if (currentIndex % columns !== 0) {
                        moveTo(currentIndex - 1);
                    }
                    break;

                case "Escape":
                case "Esc":
                    e.preventDefault();
                    closeItem(item);
                    var trigger = getTrigger(item);
                    if (trigger) trigger.focus();
                    break;

                default:
                    break;
            }
        };
    }

    function handleFocusOut(item) {
        return function (e) {
            if (!isDesktop()) return;

            var movingTo = e.relatedTarget;
            if (movingTo && item.contains(movingTo)) return;

            closeItem(item);
        };
    }

    function bindItem(item) {
        if (boundItems.has(item)) return; // never double-bind the same node
        var trigger = getTrigger(item);
        if (!trigger) return;

        boundItems.add(item);

        trigger.setAttribute("aria-expanded", "false");

        trigger.addEventListener("keydown", handleTriggerKeydown(item, trigger));
        item.addEventListener("keydown", handleSubmenuKeydown(item));
        item.addEventListener("focusout", handleFocusOut(item));

        item.addEventListener("mouseenter", function () {
            if (isDesktop()) trigger.setAttribute("aria-expanded", "true");
        });
        item.addEventListener("mouseleave", function () {
            if (isDesktop() && !item.classList.contains("js-open")) {
                trigger.setAttribute("aria-expanded", "false");
            }
        });
    }

    function bindAll() {
        getDropdownItems().forEach(bindItem);
    }

    function closeOnOutsideClick(e) {
        if (!isDesktop()) return;
        var openItems = document.querySelectorAll(".site-nav .no-mega-menu.js-open");
        openItems.forEach(function (item) {
            if (!item.contains(e.target)) closeItem(item);
        });
    }

    function closeAllOnResize() {
        if (!isDesktop()) closeAll();
    }

    function watchForNavReplacement() {
        // halo.js swaps the entire .site-nav element via jQuery replaceWith()
        // during AJAX refreshes (cart, mega-menu editor sync, etc.), which
        // silently drops any listeners bound to the old nodes. Re-bind
        // whenever the DOM under <body> changes and new dropdown items
        // appear that we haven't bound yet.
        var observer = new MutationObserver(function (mutations) {
            var shouldRebind = false;
            for (var i = 0; i < mutations.length; i++) {
                if (mutations[i].addedNodes && mutations[i].addedNodes.length) {
                    shouldRebind = true;
                    break;
                }
            }
            if (shouldRebind) bindAll();
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true
        });
    }

    function init() {
        bindAll();
        document.addEventListener("click", closeOnOutsideClick);
        window.addEventListener("resize", closeAllOnResize);
        watchForNavReplacement();
    }

    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", init);
    } else {
        init();
    }
})();